/**
 * PremiumTooltip — shared minimalistic tooltip component.
 * White background, navy (#18216D) text, small caret arrow.
 * Matches the SOWTooltip design used in SchemeOfWorkManagement.
 *
 * Props:
 *  - text       : string  — tooltip label
 *  - position   : 'top' | 'bottom' | 'right' | 'left' (default: 'top')
 *  - children   : ReactNode
 */
import React, { useState } from 'react';

const PremiumTooltip = ({ 
    text, 
    position = 'top', 
    children, 
    wrapperClassName = "relative inline-flex",
    allowWrap = false,
    maxWidth = "w-max max-w-xs sm:max-w-sm",
    textClassName = "text-[9px] font-black uppercase tracking-[0.15em]"
}) => {
    const [visible, setVisible] = useState(false);

    const positionClasses = {
        top:    'bottom-full left-1/2 -translate-x-1/2 mb-2',
        bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
        right:  'left-full top-1/2 -translate-y-1/2 ml-2',
        left:   'right-full top-1/2 -translate-y-1/2 mr-2',
    };

    const caretClasses = {
        top: `absolute left-1/2 -translate-x-1/2 top-full w-0 h-0
              border-l-[5px] border-r-[5px] border-t-[5px]
              border-l-transparent border-r-transparent border-t-white`,
        bottom: `absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0
                 border-l-[5px] border-r-[5px] border-b-[5px]
                 border-l-transparent border-r-transparent border-b-white`,
        right: `absolute right-full top-1/2 -translate-y-1/2 w-0 h-0
                border-t-[5px] border-b-[5px] border-r-[5px]
                border-t-transparent border-b-transparent border-r-white`,
        left: `absolute left-full top-1/2 -translate-y-1/2 w-0 h-0
               border-t-[5px] border-b-[5px] border-l-[5px]
               border-t-transparent border-b-transparent border-l-white`,
    };

    const wrapStyle = allowWrap 
        ? `${maxWidth} whitespace-normal break-words text-left leading-normal`
        : 'whitespace-nowrap';

    return (
        <div
            className={wrapperClassName}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
        >
            {children}
            {visible && text && (
                <div
                    className={`pointer-events-none absolute z-[300] ${wrapStyle} ${positionClasses[position]}`}
                >
                    <div className={`px-3 py-2 bg-white text-[#18216D] rounded-lg shadow-xl border border-slate-100 animate-in fade-in duration-150 ${textClassName}`}>
                        {text}
                    </div>
                    <div className={caretClasses[position]} />
                </div>
            )}
        </div>
    );
};

export default PremiumTooltip;
